/**
 * gatsby-browser.js
 *
 * Gatsby v5 / React 18 compatible.
 *
 * - wrapRootElement: wraps the entire React tree with global providers.
 *   Runs in the browser only. Mirrors gatsby-ssr.js for consistent SSR/CSR output.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

import React from "react"
import AuthProvider from "./src/providers/AuthProvider"
import { FavoritesProvider } from "./src/contexts/FavoritesContext"
import { LocationProvider } from "./src/contexts/LocationContext"
import RiaChatProvider from "./src/providers/RiaChatProvider"
import DelayedGTM from "./src/components/DelayedGTM"
import {
  trackLead,
  setPageContext,
  clickEventForHref,
} from "./src/lib/analytics"

export const wrapRootElement = ({ element }) => (
  <LocationProvider>
    <AuthProvider>
      <FavoritesProvider>
        <RiaChatProvider>
          {element}
          {/* Was previously loaded via gatsby-plugin-google-tagmanager, which injects
              an async-but-immediate GTM script into every SSR'd page — a render-blocking
              JS/TBT contributor flagged by PageSpeed. DelayedGTM already existed in the
              codebase for this but was never actually wired in anywhere; it now loads
              GTM 3s after mount instead, once the critical rendering work is done. */}
          <DelayedGTM />
        </RiaChatProvider>
      </FavoritesProvider>
    </AuthProvider>
  </LocationProvider>
)

// ─── Lead events (spec SEO-0.1) ──────────────────────────────────────────────
// Page context for every lead event comes from pageContext.seoPage, which
// gatsby-node attaches to each page it creates.
// Each page's context is remembered by path, so booking_start can look up
// the page the visitor came from without relying on the order in which
// Gatsby calls wrapPageElement and onRouteUpdate during a navigation.
let currentPath = null
const seoPageByPath = {}

export const wrapPageElement = ({ element, props }) => {
  const pathname = props.location && props.location.pathname
  const seoPage = props.pageContext && props.pageContext.seoPage
  if (pathname && seoPage) seoPageByPath[pathname] = seoPage
  if (pathname && pathname !== currentPath) {
    currentPath = pathname
    setPageContext(seoPage)
  }
  return element
}

// One delegated listener covers every WhatsApp and tel: link on the site,
// including ones rendered by editorial copy and third-party widgets.
export const onClientEntry = () => {
  document.addEventListener(
    `click`,
    e => {
      const a = e.target && e.target.closest && e.target.closest(`a[href]`)
      if (!a) return
      const event = clickEventForHref(a.getAttribute(`href`))
      if (event) trackLead(event)
    },
    true,
  )
}

export const onRouteUpdate = ({ location, prevLocation }) => {
  if (!prevLocation || !location.pathname.startsWith(`/booking`)) return
  if (prevLocation.pathname.startsWith(`/booking`)) return
  // Only a hop from a listing page counts as starting a booking.
  const previousSeoPage = seoPageByPath[prevLocation.pathname] || {}
  if (!previousSeoPage.spaceId) return
  const spaceId = new URLSearchParams(location.search).get(`spaceid`)
  trackLead(`booking_start`, {
    ...contextParams(previousSeoPage),
    space_id: spaceId || previousSeoPage.spaceId,
  })
}

function contextParams(ctx) {
  return {
    page_type: ctx.pageType,
    product: ctx.product,
    city: ctx.city,
    locality: ctx.locality,
    building_slug: ctx.buildingSlug,
  }
}
